// Logique pure de la rubrique « Paiements » : libellés et tons des états (texte coloré, jamais la
// couleur seule), montants en francs CFA avec la mention « (fictif) » (HD-02), tri des créances.
import { ETATS_CREANCE, formaterFrancs, type EtatCreance, type EtatPaiement } from '@cc/contrats';
import type { TonStatut } from '../statuts';
import type { CreanceUsager, PaiementUsager } from './types';

export const LIBELLES_ETAT_PAIEMENT: Record<EtatPaiement, string> = {
  initie: 'Initié',
  en_attente: 'En attente du prestataire',
  confirme: 'Confirmé',
  refuse: 'Refusé',
  expire: 'Expiré',
  annule: 'Annulé',
  rembourse: 'Remboursé',
};

export const TON_ETAT_PAIEMENT: Record<EtatPaiement, TonStatut> = {
  initie: 'info',
  en_attente: 'info',
  confirme: 'ok',
  refuse: 'attention',
  expire: 'attention',
  annule: 'attention',
  rembourse: 'neutre',
};

export const LIBELLES_CANAL_PAIEMENT: Record<string, string> = {
  simulateur: 'simulateur de paiement',
  mobile_money: 'mobile money',
  banque: 'paiement bancaire',
  virement: 'virement',
  terminal: 'terminal de paiement',
  caisse: 'caisse de la collectivité',
};

export const LIBELLES_SITUATION_ATTRIBUTION: Record<string, string> = {
  a_venir: 'À venir',
  en_cours: 'En cours',
  echue: 'Échue',
  resiliee: 'Résiliée',
  terminee: 'Terminée',
  attribuee: 'Attribuée',
  active: 'Active',
};

export function libelleEtatPaiement(etat: string): string {
  return LIBELLES_ETAT_PAIEMENT[etat as EtatPaiement] ?? etat;
}

export function tonEtatPaiement(etat: string): TonStatut {
  return TON_ETAT_PAIEMENT[etat as EtatPaiement] ?? 'neutre';
}

/** Un paiement attend encore la réponse du prestataire. */
export function enAttente(p: Pick<PaiementUsager, 'etat'>): boolean {
  return p.etat === 'initie' || p.etat === 'en_attente';
}

export function libelleEtatCreance(c: Pick<CreanceUsager, 'etat' | 'echue'>): string {
  if (c.echue && (c.etat === 'emise' || c.etat === 'partiellement_payee')) return 'Échéance dépassée';
  return ETATS_CREANCE[c.etat as EtatCreance] ?? c.etat;
}

export function tonEtatCreance(c: Pick<CreanceUsager, 'etat' | 'echue'>): TonStatut {
  if (c.etat === 'soldee') return 'ok';
  if (c.etat === 'annulee') return 'neutre';
  if (c.etat === 'en_contentieux' || c.echue) return 'attention';
  return 'info';
}

/** Créance qu'il reste à payer (émise ou partiellement payée, reste positif). */
export function aPayer(c: Pick<CreanceUsager, 'etat' | 'reste'>): boolean {
  return (c.etat === 'emise' || c.etat === 'partiellement_payee') && c.reste > 0;
}

export function montant(valeur: number | null | undefined, fictif = false): string {
  if (valeur === null || valeur === undefined) return 'non renseigné';
  return `${formaterFrancs(valeur)}${fictif ? ' (fictif)' : ''}`;
}

/** Montant en francs CFA pour la lecture à voix haute et les sous-titres : « 27 000 francs CFA ». */
export function francs(valeur: number): string {
  return `${valeur.toLocaleString('fr-FR')} francs CFA`;
}

/** Créances à payer d'abord, par échéance ; puis les autres, de la plus récente à la plus ancienne. */
export function trierCreances(creances: CreanceUsager[]): CreanceUsager[] {
  const ouvertes = creances.filter(aPayer).sort((a, b) => a.echeance.localeCompare(b.echeance));
  const autres = creances.filter((c) => !aPayer(c)).sort((a, b) => b.cree_le.localeCompare(a.cree_le));
  return [...ouvertes, ...autres];
}

export function totalAPayer(creances: CreanceUsager[]): number {
  return creances.filter(aPayer).reduce((s, c) => s + c.reste, 0);
}

/** Paiements en attente qui visent cette créance : leur montant est déjà réservé sur le reste. */
export function paiementsEnAttentePour(paiements: PaiementUsager[], creanceId: string): PaiementUsager[] {
  return paiements.filter((p) => enAttente(p) && p.creances_visees.includes(creanceId));
}
